/**
 * Weather condition → icon-name mapping.
 *
 * Lives in `@elara/domain` so every client (web today, native later) buckets a
 * raw weather condition into an icon identifier using identical logic — the
 * keyword-matching rules are the divergence-prone part and must stay in one
 * place. The server returns only the raw condition; it does not bucket.
 *
 * Note on portability: the *bucketing* is shared, but the returned strings are
 * presentation identifiers (Material Symbols names / `WeatherIcon` component
 * names) that happen to be the web vocabulary today. A native client that uses
 * a different icon set should map these stable bucket outputs to its own names
 * rather than re-deriving the keyword rules.
 */

const WEATHER_ICON_MAP: Record<string, string> = {
  clear: 'sunny',
  cloud: 'cloud',
  thunder: 'thunderstorm',
  drizzle: 'rainy_light',
  rain: 'rainy',
  snow: 'weather_snowy',
  mist: 'foggy',
  haze: 'foggy',
  fog: 'foggy',
};

/**
 * Maps a weather condition and description to a Material Symbols icon name.
 *
 * @param condition - The weather condition (e.g., "Clear", "Clouds")
 * @param description - The weather description (e.g., "clear sky", "few clouds")
 * @returns The Material Symbols icon name for the weather condition
 */
export function mapWeatherConditionToMaterialIcon(condition?: string, description?: string): string {
  const searchText = `${condition || ''} ${description || ''}`.toLowerCase();
  for (const [key, icon] of Object.entries(WEATHER_ICON_MAP)) {
    if (searchText.includes(key)) return icon;
  }
  return 'partly_cloudy_day';
}

/**
 * Maps a weather description to a `WeatherIcon` component name.
 *
 * @param description - The weather description (e.g., "clear sky", "few clouds", "light rain")
 * @returns The `WeatherIcon` name for the weather condition
 */
export function mapWeatherConditionToWeatherIcon(description?: string): string {
  const d = (description || '').toLowerCase();

  // Check for specific conditions in description
  if (d.includes('clear')) return 'sun';
  if (d.includes('thunder')) return 'storm';
  if (d.includes('drizzle')) return 'rain';
  if ((d.includes('rain') && d.includes('snow')) || d.includes('sleet')) return 'mixedPrecipitation';
  if (d.includes('rain')) return 'cloudRain';
  if (d.includes('snow')) return 'cloudSnow';
  if (d.includes('mist') || d.includes('haze') || d.includes('fog')) return 'fog';
  if (d.includes('few') || d.includes('scattered')) return 'partlyCloudy';
  if (d.includes('broken') || d.includes('overcast')) return 'cloudy';
  if (d.includes('cloud')) return 'cloudy';

  return 'partlyCloudy';
}
